import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { push as Push } from 'redux-first-history';
import { useTranslation } from 'react-i18next';

import { CampaignIcon } from '@dev/ee-components';
import { safelyEncodeBase64Url } from '@dev/ee-tools';

import { useCurrentPlanStatus } from '@/services/hooks/useCurrentPlanStatus';
import { CampaignCreatorType, CampaignsPaths, pathsParams } from '@/services/dictionaries/Paths';
import { SaveOnboardingStart, getOnboardingStore } from '@/store/onboarding/actions';
import { GetCampaignByName, getCampaignByNameStore } from '@/store/campaigns/actions';

import { OnboardingVideoThumbnail } from '@/components/Onboarding/components/OnboardingVideoThumbnail';
import { OnboardingTask, OnboardingSteps, OnboardingTaskProps } from '../../../types';
import SvgCampaignReport from './svgs/svg-camapign-report.svg';

const taskId = 'sendEmailTask';
const itemVideoId = 'X2HZ_cpQN0M';

export enum SendEmailTaskSavedDataKeysEnum {
    SendEmailCampaignsReportReview = 'SendEmailCampaignsReportReview',
    SendEmailSkipStep1 = 'SendEmailSkipStep1',
    SendEmailSkipStep2 = 'SendEmailSkipStep2',
}

export const useSendEmailTask = ({ onboardingGoals }: OnboardingTaskProps): OnboardingTask => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { savedData, data } = useSelector(getOnboardingStore);
    const campaign = useSelector(getCampaignByNameStore);
    const { isPilotPlan } = useCurrentPlanStatus();

    const createaCampaignPrimaryActionCallback = (): void => {
        dispatch(Push(CampaignsPaths.CampaignCreator.replace(pathsParams.type, CampaignCreatorType.REGULAR)));
    };

    useEffect(() => {
        if (!data?.FirstCampaign?.Name || data?.FirstCampaign?.Name === campaign?.Name) return;

        dispatch(
            GetCampaignByName({
                name: data?.FirstCampaign?.Name,
                isLoadReportsRequired: false,
            })
        );
    }, [data?.FirstCampaign?.Name]);

    const reviewCampaignPrimaryActionCallback = (): void => {
        dispatch(SaveOnboardingStart({ SendEmailCampaignsReportReview: true }));
        dispatch(
            Push(
                CampaignsPaths.CampaignOverview.replace(pathsParams.type, campaign?.TriggerOptions?.Type).replace(
                    pathsParams.name,
                    safelyEncodeBase64Url(data?.FirstCampaign?.Name)
                )
            )
        );
    };

    const sendEmailSteps: OnboardingSteps = {
        id: taskId,
        title: t('sendemailsteps'),
        items: [
            {
                title: t('createacampaign'),
                features: [t('createcampaign_feature1'), t('createcampaign_feature2')],
                ...(!isPilotPlan && { tip: t('nocontactsyoucansend') }),
                secondaryContent: <OnboardingVideoThumbnail videoId={itemVideoId} />,
                primaryActionTitle: t('createcampaign'),
                primaryActionCallback: createaCampaignPrimaryActionCallback,
                isSkipped: !!savedData?.SendEmailSkipStep1,
                skipSavedDataKey: SendEmailTaskSavedDataKeysEnum.SendEmailSkipStep1,
                isCompleted: data?.CompletedCampaign,
            },
            {
                title: t('reviewcampaignsreport'),
                features: [t('reviewcampaignsreport_feature1'), t('reviewcampaignsreport_feature2')],
                secondaryContent: <SvgCampaignReport />,
                primaryActionTitle: t('viewreport'),
                primaryActionCallback: reviewCampaignPrimaryActionCallback,
                isNotReady: !!savedData?.SendEmailSkipStep1 || !data?.CompletedCampaign,
                isSkipped: !!savedData?.SendEmailSkipStep2,
                skipSavedDataKey: SendEmailTaskSavedDataKeysEnum.SendEmailSkipStep2,
                isCompleted: !!savedData?.SendEmailCampaignsReportReview,
            },
        ],
    };

    return {
        header: t('sendyourfirstemail'),
        icon: <CampaignIcon />,
        title: t('sendfirstemail_title'),
        description: t('sendfirstemail_desc'),
        steps: [sendEmailSteps],
        activeStepsIndex: 0,
        isVisible:
            onboardingGoals.isWantExploreAnsweredOnly ||
            onboardingGoals.isSendingMarketingEmailsAnswered ||
            onboardingGoals.isSendingNewslettersAnswered,
    };
};
